import React from 'react';
import { observer } from 'mobx-react';

import adminStore from '~/stores/adminStore';
import authStore from '~/stores/authStore';

import Hidden from '@material-ui/core/Hidden';
import Button from '@material-ui/core/Button';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import {
    prices,
    matchPrice,
    matchTierPrice,
    PROMO_GRADIENT,
    WTM_PROMO_ACTIVE,
    WTM_OLD_PRICE_MULTIPLIER,
    WTM_PROMO_MIN_ACCOUNTS
} from './Calculator';

const WTM_TINT = 'linear-gradient(180deg, rgba(37,211,102,0.05), rgba(0,136,204,0.05), rgba(115,60,228,0.05))';

const langLib = {
    en: {
        numbers: 'Numbers',
        price: 'Price (30 days)',
        cost: 'Total Cost',
        from: 'from',
        selectThis: 'Select this',
        promo: 'PROMO {n}+'
    },
    ru: {
        numbers: 'Количество',
        price: 'за номер (30 дней)',
        cost: 'Сумма',
        from: 'от',
        selectThis: 'Выбрать',
        promo: 'АКЦИЯ ОТ {n}'
    }
} as const;

const TablePrice = ({ extended }: { extended?: boolean }) => {
    const { phoneNumbers, priceType } = adminStore;
    const { lang } = authStore;
    const lib = langLib[lang];

    const promoLabel = lib['promo'].replace('{n}', String(WTM_PROMO_MIN_ACCOUNTS));

    return (
        <Table size="medium">
            <TableHead>
                <TableRow>
                    <TableCell>{lib['numbers']}</TableCell>
                    {extended && (
                        <>
                            <TableCell align="right">Whatsapp</TableCell>
                            <TableCell align="right">MAX (Vk)</TableCell>
                            <TableCell align="right">Telegram</TableCell>
                            <TableCell align="right" style={WTM_PROMO_ACTIVE ? {
                                background: 'linear-gradient(180deg, rgba(37,211,102,0.1), rgba(0,136,204,0.1), rgba(115,60,228,0.1))'
                            } : undefined}>
                                <Hidden xsDown>Whatsapp + Telegram + MAX</Hidden>
                                <Hidden smUp>WA+TG+MAX</Hidden>
                                {WTM_PROMO_ACTIVE && (
                                    <span style={{
                                        display: 'block',
                                        width: 'fit-content',
                                        marginLeft: 'auto',
                                        fontSize: '0.65rem',
                                        background: '#fb4934',
                                        color: '#fff',
                                        borderRadius: 4,
                                        padding: '2px 6px',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                        marginTop: 4
                                    }}>{promoLabel}</span>
                                )}
                            </TableCell>
                        </>
                    )}
                    {!extended && (
                        <>
                            <TableCell align="right">{lib['price']}</TableCell>
                            <TableCell align="right">{lib['cost']}</TableCell>
                            <Hidden xsDown>
                                <TableCell align="right" />
                            </Hidden>
                        </>
                    )}
                </TableRow>
            </TableHead>
            <TableBody>
                {prices[lang].slice(1).map(({ [priceType]: price, priceT, priceW, priceM, priceWT, priceTM, priceWTM, numbers }) => {
                    const isActive = matchTierPrice(lang, priceType, phoneNumbers) === price;
                    // Цена, которую пользователь реально заплатит за это количество:
                    // для WA + TG + MAX ниже порога акции она полная.
                    const rowPrice = matchPrice(lang, priceType, numbers);
                    const promoRow = WTM_PROMO_ACTIVE && numbers >= WTM_PROMO_MIN_ACCOUNTS;
                    const promoStart = WTM_PROMO_ACTIVE && numbers === WTM_PROMO_MIN_ACCOUNTS;
                    // Строка, с которой начинается акция, получает 2px фирменного градиента сверху.
                    const wtmCellStyle = promoStart
                        ? { background: `${PROMO_GRADIENT} top left / 100% 2px no-repeat, ${WTM_TINT}` }
                        : promoRow
                        ? { background: WTM_TINT }
                        : undefined;

                    return (
                        <TableRow key={numbers} selected={isActive}>
                            <TableCell component="th" scope="row">
                                {lib['from']} <b>{numbers}</b>
                            </TableCell>
                            {extended && (
                                <>
                                    <TableCell align="right">{authStore.printCost(priceW)}</TableCell>
                                    <TableCell align="right">{authStore.printCost(priceM)}</TableCell>
                                    <TableCell align="right">{authStore.printCost(priceT)}</TableCell>
                                    <TableCell align="right" style={wtmCellStyle}>
                                        {promoRow && (
                                            <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em', marginRight: 4 }}>
                                                {authStore.printCost(priceWTM * WTM_OLD_PRICE_MULTIPLIER)}
                                            </span>
                                        )}
                                        <b style={promoRow ? { color: '#fb4934' } : undefined}>
                                            {authStore.printCost(promoRow ? priceWTM : priceWTM * WTM_OLD_PRICE_MULTIPLIER)}
                                        </b>
                                    </TableCell>
                                </>
                            )}
                            {!extended && (
                                <>
                                    <TableCell align="right">{authStore.printCost(rowPrice)}</TableCell>
                                    <TableCell align="right">
                                        {authStore.printCost(Number((rowPrice * numbers).toFixed(2)))}
                                    </TableCell>
                                    <Hidden xsDown>
                                        <TableCell align="right">
                                            <Button
                                                onClick={() => adminStore.setPhoneNumbers(numbers)}
                                                color="primary"
                                                variant={isActive ? 'contained' : 'outlined'}
                                                size="small"
                                            >
                                                {lib['selectThis']}
                                            </Button>
                                        </TableCell>
                                    </Hidden>
                                </>
                            )}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

export default observer(TablePrice);
